import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContext";
import Header from "@/components/Header";
import {
  Search,
  FileText,
  Share2,
  Megaphone,
  Palette,
  Globe,
  User,
  LogOut,
  Crown,
  Settings,
  BarChart3,
  TrendingUp,
  Zap,
  DollarSign,
  Eye,
  MousePointer,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  RefreshCw,
  Shield
} from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: contextUser, setUserData, loading: contextLoading } = useUser();
  
  // Initialize user from multiple sources
  const getInitialUser = () => {
    if (location.state?.user) return location.state.user;
    if (contextUser) return contextUser;
    try {
      const cached = localStorage.getItem("markeemix_user");
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return null;
  };
  
  const [user, setUser] = useState(getInitialUser);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If user data passed from AuthCallback, save to context
    if (location.state?.user) {
      setUser(location.state.user);
      setUserData(location.state.user);
      return;
    }
    
    // If context has user, use that
    if (contextUser) {
      setUser(contextUser);
      return;
    }

    // If context is still loading, wait
    if (contextLoading) {
      return;
    }

    // Try to get from localStorage as final fallback
    try {
      const cachedUser = localStorage.getItem("markeemix_user");
      if (cachedUser) {
        const userData = JSON.parse(cachedUser);
        setUser(userData);
        return;
      }
    } catch (e) {
      console.error("Failed to parse cached user:", e);
    }
  }, [location.state, contextUser, contextLoading, setUserData]);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Set logout flag and clear storage
    sessionStorage.setItem("markeemix_logged_out", "true");
    localStorage.removeItem("markeemix_user");
    localStorage.removeItem("markeemix_session_token");
    window.location.href = "/";
  };

  const quickTools = [
    { name: "YouTube CRM Studio", icon: <Eye className="w-6 h-6" />, path: "/studio", color: "bg-red-600", highlight: true },
    { name: "AI SEO Assistant", icon: <Zap className="w-6 h-6" />, path: "/studio/seo", color: "bg-violet-500" },
    { name: "AI Topic Suggestions", icon: <Target className="w-6 h-6" />, path: "/studio/topic-suggestions", color: "bg-pink-500" },
    { name: "SEO Tools", icon: <Search className="w-6 h-6" />, path: "/tools/seo", color: "bg-blue-500" },
    { name: "YouTube SEO", icon: <Eye className="w-6 h-6" />, path: "/tools/youtube", color: "bg-red-500" },
    { name: "Content Marketing", icon: <FileText className="w-6 h-6" />, path: "/tools/content", color: "bg-purple-500" },
    { name: "Social Media", icon: <Share2 className="w-6 h-6" />, path: "/tools/social-media", color: "bg-pink-500" },
    { name: "Facebook Ads", icon: <Megaphone className="w-6 h-6" />, path: "/tools/facebook-ads", color: "bg-sky-500" },
    { name: "Design Tools", icon: <Palette className="w-6 h-6" />, path: "/tools/design", color: "bg-orange-500" }
  ];

  // User stats (fetched from API)
  const [userStats, setUserStats] = useState(null);
  const [recentHistory, setRecentHistory] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [usageData, setUsageData] = useState(null);
  const [seoHealth, setSeoHealth] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      setStatsLoading(true);
      try {
        // Fetch search history
        const historyRes = await axios.get(`${BACKEND_URL}/api/user/search-history?limit=10`, { withCredentials: true });
        setRecentHistory(historyRes.data.history || []);
        
        // Fetch usage stats
        const usageRes = await axios.get(`${BACKEND_URL}/api/keywords/usage-status`, { withCredentials: true }).catch(() => ({ data: null }));
        setUsageData(usageRes.data);
        
        // Fetch latest site audit for SEO health (if any)
        const auditRes = await axios.get(`${BACKEND_URL}/api/site-audit/history?limit=1`, { withCredentials: true }).catch(() => ({ data: { audits: [] } }));
        if (auditRes.data.audits?.length > 0) {
          setSeoHealth(auditRes.data.audits[0]);
        }
        
        setUserStats({
          totalSearches: historyRes.data.total || 0
        });
      } catch (e) {
        console.error("Failed to fetch user data:", e);
        setRecentHistory([]);
        setUserStats({ totalSearches: 0 });
      }
      setStatsLoading(false);
    };
    fetchUserData();
  }, [user]);

  const getPlanBadge = (plan) => {
    switch(plan) {
      case 'pro':
        return <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white">Pro</Badge>;
      case 'enterprise':
        return <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white">Enterprise</Badge>;
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show login prompt if not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-violet-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Login Required</h2>
          <p className="text-gray-600 mb-6">Please sign in to access your dashboard and marketing tools.</p>
          <Button 
            className="w-full bg-violet-700 hover:bg-violet-800"
            onClick={() => navigate("/login")}
          >
            Sign In to Continue
          </Button>
          <p className="mt-4 text-sm text-gray-500">
            Don't have an account? <span className="text-violet-600 cursor-pointer hover:underline" onClick={() => navigate("/register")}>Register here</span>
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <img 
                src="/logo-light.png" 
                alt="MarkeeMix" 
                className="h-8 w-auto cursor-pointer"
                onClick={() => navigate("/")}
                data-testid="dashboard-logo"
              />
            </div>
            
            <div className="flex items-center gap-4">
              {user?.plan === 'free' && !user?.trial_used && (
                <Button 
                  onClick={async () => {
                    try {
                      const response = await axios.post(`${BACKEND_URL}/api/auth/start-trial`, {}, { withCredentials: true });
                      if (response.data.success) {
                        toast.success("7-day free trial activated! Enjoy Pro features!");
                        setUser({ ...user, plan: "pro", trial_active: true });
                      }
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to start trial");
                    }
                  }}
                  className="hidden md:flex bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                  data-testid="start-trial-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Start Free Trial
                </Button>
              )}
              {user?.plan !== 'enterprise' && user?.plan !== 'pro' && (
                <Button 
                  variant="outline"
                  onClick={() => navigate("/pricing")}
                  className="hidden md:flex"
                  data-testid="upgrade-btn"
                >
                  <Crown className="w-4 h-4 mr-2 text-yellow-500" />
                  Upgrade
                </Button>
              )}
              
              <div className="flex items-center gap-3">
                <div className="hidden md:block text-right">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    {getPlanBadge(user?.plan)}
                  </div>
                </div>
                
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    className="w-10 h-10 rounded-full border-2 border-violet-200"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-violet-600" />
                  </div>
                )}
                
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! 👋
            </h1>
            <p className="text-gray-600 mt-2">
              Here's your marketing command center. What would you like to work on today?
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/security")}>
              <Shield className="w-4 h-4 mr-2" />
              Security
            </Button>
            <Button variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Data
            </Button>
          </div>
        </div>

        {/* Trial Banner */}
        {user?.trial_active && user?.trial_ends && (
          <Card className="mb-8 p-4 bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                  <Zap className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-violet-900">Pro Trial Active</p>
                  <p className="text-sm text-violet-700">
                    Ends: {new Date(user.trial_ends).toLocaleDateString()} 
                    ({Math.ceil((new Date(user.trial_ends) - new Date()) / (1000 * 60 * 60 * 24))} days left)
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate("/pricing")}
                className="bg-violet-600 hover:bg-violet-700"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade Now
              </Button>
            </div>
          </Card>
        )}

        {/* YouTube Studio CRM banner */}
        <Card
          className="p-5 mb-8 cursor-pointer border-red-200 bg-gradient-to-r from-red-50 via-violet-50 to-pink-50 hover:shadow-lg transition-all"
          onClick={() => navigate("/studio")}
          data-testid="dashboard-studio-banner"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-violet-600 flex items-center justify-center text-white shadow-lg">
              <Eye className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-gray-900">YouTube CRM Studio</h3>
                <Badge className="bg-red-500 text-white">LIVE</Badge>
              </div>
              <p className="text-sm text-gray-600">
                Full workflow CRM — pipeline, AI SEO, channel memory, live feed, super-admin god view, WhatsApp ops.
              </p>
            </div>
            <Button className="bg-red-600 hover:bg-red-700 text-white">
              Open Studio <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </Card>

        {/* Quick Access Tools */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Access</h2>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {quickTools.map((tool, index) => (
              <Card 
                key={index}
                className="p-4 cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1"
                onClick={() => navigate(tool.path)}
              >
                <div className={`w-12 h-12 ${tool.color} rounded-lg flex items-center justify-center text-white mb-3`}>
                  {tool.icon}
                </div>
                <p className="font-medium text-gray-900">{tool.name}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Analytics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {/* Usage Stats Card */}
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Daily Searches</p>
                <p className="text-2xl font-bold text-blue-900">
                  {usageData ? `${usageData.used}/${usageData.limit === -1 ? '∞' : usageData.limit}` : '-/-'}
                </p>
                <p className="text-xs text-blue-500 mt-1">
                  {usageData?.remaining > 0 ? `${usageData.remaining} remaining` : 'Limit reached'}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                <Search className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>

          {/* SEO Health Score */}
          <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">SEO Health</p>
                <p className="text-2xl font-bold text-green-900">
                  {seoHealth?.overall_score || seoHealth?.seo_score || '--'}/100
                </p>
                <p className="text-xs text-green-500 mt-1">
                  {seoHealth?.url ? seoHealth.url.replace(/https?:\/\//, '').split('/')[0] : 'Run Site Audit'}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>

          {/* Total Searches */}
          <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Total Searches</p>
                <p className="text-2xl font-bold text-purple-900">{userStats?.totalSearches || 0}</p>
                <p className="text-xs text-purple-500 mt-1">All time</p>
              </div>
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>

          {/* Plan Status */}
          <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">Your Plan</p>
                <p className="text-2xl font-bold text-amber-900 capitalize">{user?.plan || 'Free'}</p>
                <p className="text-xs text-amber-500 mt-1">
                  {user?.plan === 'pro' ? '300 searches/mo' : user?.plan === 'enterprise' ? 'Unlimited' : '3 searches/day'}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>
        </div>

        {/* User Activity Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Recent Search History */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Search className="w-5 h-5" /> Your Recent Activity
            </h3>
            {statsLoading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : recentHistory.length > 0 ? (
              <div className="space-y-3">
                {recentHistory.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-800">{item.query}</p>
                      <p className="text-sm text-gray-500">{item.tool}</p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(item.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No activity yet</p>
                <p className="text-sm">Start using tools to see your history here</p>
              </div>
            )}
          </Card>

          {/* Account Status */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" /> Account Status
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Plan</span>
                {getPlanBadge(user?.plan)}
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Email</span>
                <span className="font-medium">{user?.email}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Member Since</span>
                <span className="font-medium">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Total Searches</span>
                <span className="font-medium">{userStats?.totalSearches || 0}</span>
              </div>
              {user?.plan === "free" && (
                <Button 
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600"
                  onClick={() => navigate("/pricing")}
                >
                  <Crown className="w-4 h-4 mr-2" /> Upgrade to Pro
                </Button>
              )}
            </div>
          </Card>
        </div>

        {/* Upgrade CTA for Free Users */}
        {user?.plan === 'free' && (
          <Card className="p-6 bg-gradient-to-r from-violet-600 to-purple-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">Unlock Premium Features</h3>
                </div>
                <p className="text-violet-100">
                  Get unlimited access to AI content generation, advanced analytics, and priority support.
                </p>
              </div>
              <Button 
                className="bg-white text-violet-600 hover:bg-violet-50"
                onClick={() => navigate("/pricing")}
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade Now
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
